import { call, select } from 'redux-saga/effects';
import { selectFeatureFlag } from 'features/feature-set/featuresSlice';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import {
  selectBreakoutRoomFreeMovement,
  selectBreakoutRoomsStatus,
  selectIsBreakoutRoom,
} from 'features/breakout-rooms/breakoutRoomsSlice';
import { selectIsSystemSession } from 'features/application/selectors';
import { selectPrivateGroupChat } from 'features/chat/chatSlice';
import { selectLocalUserRole } from 'features/users/usersSlice';
import { SidePanelType } from 'features/side-panels/types';
import { canAccessChat } from 'features/chat/canAccessChat';
import { canAccessQA } from 'features/qa/canAccessQA';
import { canAccessPolls } from 'features/polls/canAccessPolls';
import { canAccessParticipants } from 'features/users/canAccessParticipants';
import { canAccessSharedNotes } from 'features/shared-notes/canAccessSharedNotes';
import { canAccessContentLibrary } from 'features/content-library/canAccessContentLibrary';

export function* isParticipantsPanelAccessible(): Generator<any, boolean> {
  const participantsListEnabled: boolean = yield select(selectFeatureFlag, 'participantsList');
  const canSeeParticipantsPanel: boolean = yield call(
    hasPermissionsSaga,
    PermissionTypes.seeParticipantsPanel
  );
  const breakoutRoomsEnabled: boolean = yield select(selectFeatureFlag, 'breakoutRooms');
  const canManageBreakout: boolean = yield call(hasPermissionsSaga, PermissionTypes.manageBreakout);
  const breakoutStatus: string = yield select(selectBreakoutRoomsStatus);
  const breakoutFreeMovement: boolean = yield select(selectBreakoutRoomFreeMovement);
  const isSystemSession: boolean = yield select(selectIsSystemSession);

  return canAccessParticipants({
    participantsListEnabled,
    canSeeParticipantsPanel,
    breakoutRoomsEnabled,
    canManageBreakout,
    breakoutStatus,
    breakoutFreeMovement,
    isSystemSession,
  });
}

function* isPanelAccessible(panel: SidePanelType): Generator<any, boolean> {
  switch (panel) {
    case 'chat': {
      const generalChatEnabled: boolean = yield select(selectFeatureFlag, 'generalChat');
      const canChatInGeneral: boolean = yield call(hasPermissionsSaga, PermissionTypes.generalChat);
      const privateChatEnabled: boolean = yield select(selectFeatureFlag, 'privateChat');
      const canChatInPrivate: boolean = yield call(hasPermissionsSaga, PermissionTypes.privateChat);
      const privateGroupChatEnabled: boolean = yield select(selectFeatureFlag, 'privateGroupChat');
      const privateGroupChat: { roles: string[] } = yield select(selectPrivateGroupChat);
      const localUserRole: string = yield select(selectLocalUserRole);
      const isBreakoutRoom: boolean = yield select(selectIsBreakoutRoom);
      const isSystemSession: boolean = yield select(selectIsSystemSession);

      return canAccessChat({
        generalChatEnabled,
        canChatInGeneral,
        privateChatEnabled,
        canChatInPrivate,
        privateGroupChatEnabled,
        privateGroupChatRoles: privateGroupChat.roles,
        localUserRole,
        isBreakoutRoom,
        isSystemSession,
      }).chatAvailable;
    }
    case 'qa': {
      const qaEnabled: boolean = yield select(selectFeatureFlag, 'qa');
      const canAccessQa: boolean = yield call(hasPermissionsSaga, PermissionTypes.accessQa);
      const isSystemSession: boolean = yield select(selectIsSystemSession);

      return canAccessQA({ qaEnabled, canAccessQa, isSystemSession });
    }
    case 'polls': {
      const pollsEnabled: boolean = yield select(selectFeatureFlag, 'polls');
      const canManagePolls: boolean = yield call(hasPermissionsSaga, PermissionTypes.managePolls);
      const canTakePolls: boolean = yield call(hasPermissionsSaga, PermissionTypes.takePolls);
      const isBreakoutRoom: boolean = yield select(selectIsBreakoutRoom);
      const isSystemSession: boolean = yield select(selectIsSystemSession);

      return canAccessPolls({
        pollsEnabled,
        canManagePolls,
        canTakePolls,
        isBreakoutRoom,
        isSystemSession,
      });
    }
    case 'users': {
      return yield call(isParticipantsPanelAccessible);
    }
    case 'shared-notes': {
      const sharedNotesEnabled: boolean = yield select(selectFeatureFlag, 'sharedNotes');
      const canSeeNotes: boolean = yield call(hasPermissionsSaga, PermissionTypes.seeNotes);
      const canEditNotes: boolean = yield call(
        hasPermissionsSaga,
        PermissionTypes.collaborateInNotes
      );
      const isSystemSession: boolean = yield select(selectIsSystemSession);

      return canAccessSharedNotes({
        sharedNotesEnabled,
        canSeeNotes,
        canEditNotes,
        isSystemSession,
      });
    }
    case 'content-library': {
      const contentLibraryEnabled: boolean = yield select(selectFeatureFlag, 'contentLibrary');

      return canAccessContentLibrary({ contentLibraryEnabled });
    }
    default:
      return false;
  }
}

export function* filterAccessiblePanels(panels: SidePanelType[]): Generator<any, SidePanelType[]> {
  const accessible: SidePanelType[] = [];

  for (const panel of panels) {
    const isAccessible: boolean = yield call(isPanelAccessible, panel);
    if (isAccessible) {
      accessible.push(panel);
    }
  }

  return accessible;
}
